import React from "react";
import { connect } from "react-redux";
import { toastr } from "react-redux-toastr";
import moment from "moment";
import {
	FiSend,
	FiRefreshCw,
	FiPlus,
	FiDownload,
	FiX,
	FiFileText,
	FiEdit3,
	FiMessageCircle,
} from "react-icons/fi";
import { BsEmojiSmile } from "react-icons/bs";
import Dropzone from "react-dropzone";
import { timesService, chatsService } from "services";
import { authService } from "services";
import { errorFormating } from "utils/utils";
import Avatar from "components/Avatar";
import {
	getTimeChatId,
	getChatMessages,
	getMessageText,
	getMessageAuthorId,
	getMessageCreatedAt,
	getMessageIsNote,
	getEmployeeDisplayName,
	getAttachmentAuthorId,
	getAttachmentAuthor,
	getAttachmentCreatedAt,
	loadStoredAttachmentAuthors,
	mergeStoredAttachmentAuthors,
	extractAttachmentsFromResponse,
	isTempTimeRegistration,
	normalizeChatResponse,
} from "utils/timeChatUtils";
import { withTranslation } from "react-i18next";
import "./TimeChatModal.scss";

const QUICK_EMOJIS = ["👍", "😊", "🙏", "✅", "❤️", "😂"];

class TimeObservationEdit extends React.Component {
	constructor(props) {
		super(props);

		this.messagesEndRef = React.createRef();
		this.messagesContainerRef = React.createRef();
		this.composerRef = React.createRef();

		this.state = {
			isReadonly: props.isReadonly !== undefined && props.isReadonly,
			isLoadingChat: false,
			isSendingMessage: false,
			showEmojiPicker: false,
			isNote: false,
			time: props.time,
			chat: null,
			draftMessage: "",
			files: [],
			attachmentAuthors: loadStoredAttachmentAuthors(props.time?.id),
			chatListSyncNeeded: false,
		};
	}

	componentDidMount() {
		this.loadChat();
		this.refreshTimeAttachments();
	}

	componentDidUpdate(prevProps, prevState) {
		const prevTimelineLength = this.buildTimelineItems(prevState).length;
		const nextTimelineLength = this.buildTimelineItems(this.state).length;
		if (nextTimelineLength > prevTimelineLength) {
			this.scrollToBottom();
		}
	}

	scrollToBottom = () => {
		const scroll = () => {
			const container = this.messagesContainerRef.current;
			if (container) {
				container.scrollTop = container.scrollHeight;
			}
		};

		requestAnimationFrame(() => {
			requestAnimationFrame(scroll);
		});
	};

	handleSyncChat = () => {
		this.loadChat({ fromSync: true });
		this.refreshTimeAttachments();
	};

	getActiveChatId = () =>
		getTimeChatId(this.state.time) ?? this.state.chat?.id ?? null;

	loadChat = async ({ fromSync = false } = {}) => {
		const chatId = this.getActiveChatId();
		if (!chatId) return;

		this.setState({ isLoadingChat: true });
		try {
			const response = await chatsService.getById(chatId);
			if (!response.ok) {
				if (fromSync) {
					toastr.error(
						"Error",
						this.getErrorMessage(
							errorFormating(response),
							this.props.t("times.chat.errorSyncing")
						)
					);
				}
				this.setState({ isLoadingChat: false });
				return;
			}

			const chat = normalizeChatResponse(response);
			const prevCount = getChatMessages(this.state.chat).length;
			const nextCount = getChatMessages(chat).length;

			this.setState({ chat, isLoadingChat: false }, () => {
				if (fromSync || nextCount > prevCount) {
					this.scrollToBottom();
				}
			});

			if (fromSync) {
				if (nextCount > prevCount) {
					toastr.success(
						this.props.t("common.success"),
						this.props.t("times.chat.syncedNewMessages")
					);
				} else {
					toastr.info(
						this.props.t("common.info"),
						this.props.t("times.chat.syncedUpToDate")
					);
				}
			}
		} catch {
			this.setState({ isLoadingChat: false });
		}
	};

	refreshTimeAttachments = async () => {
		const { time } = this.state;
		if (!time?.id || this.isTimeUnsaved()) return [];

		try {
			const response = await timesService.getById(time.id);
			if (!response.ok) return [];

			const data = response.data ?? response;
			const attachments = Array.isArray(data.attachments) ? data.attachments : [];

			this.setState((prev) => ({
				time: {
					...prev.time,
					attachments,
				},
			}));

			return attachments;
		} catch {
			return [];
		}
	};

	handleCloseModal = () => {
		if (this.state.chatListSyncNeeded && typeof this.props.getTimeList === "function") {
			this.props.getTimeList();
		}
		this.props.handleClose();
	};

	getCurrentUserId = () => {
		return (
			this.props.user?.id ??
			authService.currentUser?.id ??
			null
		);
	};

	getEmployeeForHeader = () => {
		const { time } = this.state;
		const { employee: propEmployee } = this.props;
		return time?.employee || propEmployee || null;
	};

	getChatParticipantsById = () => {
		const byId = {};

		getChatMessages(this.state.chat).forEach((message) => {
			const id = getMessageAuthorId(message);
			const person = message.employee || message.author;
			if (id != null && person) {
				byId[String(id)] = person;
			}
		});

		return byId;
	};

	getAttachmentAuthorIdForRender = (attachment) => {
		const trackedAuthorId = this.state.attachmentAuthors?.[attachment?.url];
		if (trackedAuthorId != null) return trackedAuthorId;
		return getAttachmentAuthorId(attachment);
	};

	getAttachmentAuthorForRender = (attachment) => {
		const authorId = this.getAttachmentAuthorIdForRender(attachment);
		const apiAuthor = getAttachmentAuthor(attachment);
		const apiAuthorId = getAttachmentAuthorId(attachment);

		if (apiAuthor && apiAuthorId != null && String(apiAuthorId) === String(authorId)) {
			return apiAuthor;
		}

		if (authorId != null) {
			const fromChat = this.getChatParticipantsById()[String(authorId)];
			if (fromChat) return fromChat;

			const currentUser = this.props.user || authService.currentUser;
			if (
				currentUser?.id != null &&
				String(currentUser.id) === String(authorId)
			) {
				return currentUser;
			}
		}

		return null;
	};

	persistAttachmentAuthors = (updates) => {
		const { time } = this.state;
		if (!time?.id || !updates || !Object.keys(updates).length) return;

		const nextAuthors = mergeStoredAttachmentAuthors(time.id, updates);
		this.setState({ attachmentAuthors: nextAuthors });
	};

	trackUploadedAttachmentAuthors = (uploadedFileNames, attachments) => {
		const currentUserId = this.getCurrentUserId();
		if (!currentUserId || !uploadedFileNames.length) return;

		const uploadedNames = new Set(uploadedFileNames);
		const updates = {};

		attachments.forEach((attachment) => {
			if (!attachment?.url) return;

			const apiAuthorId = getAttachmentAuthorId(attachment);
			if (apiAuthorId != null) {
				updates[attachment.url] = apiAuthorId;
				return;
			}

			if (uploadedNames.has(attachment.name)) {
				updates[attachment.url] = currentUserId;
			}
		});

		this.persistAttachmentAuthors(updates);
	};

	isTimeUnsaved = () => isTempTimeRegistration(this.state.time);

	getErrorMessage = (errorLike, fallbackMessage) => {
		if (!errorLike) return fallbackMessage;
		if (typeof errorLike === "string") return errorLike;
		if (errorLike.description) return errorLike.description;
		if (Array.isArray(errorLike.errors) && errorLike.errors.length > 0) {
			return errorLike.errors[0]?.description || fallbackMessage;
		}
		if (errorLike.general) return errorLike.general;
		return fallbackMessage;
	};

	handleDraftChange = (event) => {
		this.setState({ draftMessage: event.target.value });
	};

	handleDraftKeyDown = (event) => {
		if (event.key === "Enter") {
			event.preventDefault();
			this.handleSendMessage();
		}
	};

	handleComposerModeChange = (isNote) => {
		this.setState({ isNote, showEmojiPicker: false }, () => {
			this.composerRef.current?.focus();
		});
	};

	toggleEmojiPicker = () => {
		this.setState((prev) => ({ showEmojiPicker: !prev.showEmojiPicker }));
	};

	appendEmoji = (emoji) => {
		this.setState((prev) => ({
			draftMessage: `${prev.draftMessage}${emoji}`,
			showEmojiPicker: false,
		}));
		if (this.composerRef.current) {
			this.composerRef.current.focus();
		}
	};

	uploadPendingFiles = async () => {
		const { files, time } = this.state;
		if (!files.length) return true;

		const uploadedFileNames = files.map((file) => file.name);
		const formData = new FormData();
		files.forEach((file) => formData.append("filename", file));

		const response = await timesService.updateAttachments(time.id, formData);
		if (!response.ok) {
			toastr.error(
				"Error",
				this.getErrorMessage(
					errorFormating(response),
					this.props.t("times.observationsModal.errorSaving")
				)
			);
			return false;
		}

		const attachments = await this.refreshTimeAttachments();
		const responseAttachments = extractAttachmentsFromResponse(response);
		const mergedAttachments =
			responseAttachments.length > 0 ? responseAttachments : attachments;
		this.trackUploadedAttachmentAuthors(uploadedFileNames, mergedAttachments);
		this.setState({ files: [], chatListSyncNeeded: true });
		return true;
	};

	sendChatMessage = async (trimmed, time, isNote) => {
		const chatId = getTimeChatId(time);
		const body = { message: trimmed, is_note: isNote };
		let response;

		if (chatId) {
			response = await chatsService.addMessage(chatId, body);
		} else {
			response = await chatsService.createForTimeRegistration(time.id, body);
		}

		if (!response.ok) {
			toastr.error(
				"Error",
				this.getErrorMessage(
					errorFormating(response),
					this.props.t("times.chat.errorSending")
				)
			);
			return null;
		}

		return normalizeChatResponse(response);
	};

	handleSendMessage = async () => {
		const { draftMessage, time, files, isReadonly, isNote } = this.state;
		if (isReadonly) return;

		if (this.isTimeUnsaved()) {
			toastr.warning("", this.props.t("times.chat.saveTimeBeforeMessage"));
			return;
		}

		const trimmed = draftMessage.trim();
		const hasFiles = !isNote && files.length > 0;
		if (!trimmed && !hasFiles) return;

		this.setState({ isSendingMessage: true });
		try {
			if (hasFiles) {
				const uploaded = await this.uploadPendingFiles();
				if (!uploaded) return;
			}

			if (trimmed) {
				const chat = await this.sendChatMessage(trimmed, time, isNote);
				if (!chat) return;

				this.setState((prev) => ({
					chat,
					time: {
						...prev.time,
						chat_id: chat.id,
						chatId: chat.id,
					},
					chatListSyncNeeded: true,
				}));
			}

			this.setState({
				draftMessage: "",
				showEmojiPicker: false,
			});
		} finally {
			this.setState({ isSendingMessage: false });
		}
	};

	onDrop = (acceptedFiles) => {
		if (!acceptedFiles?.length) return;

		if (this.isTimeUnsaved()) {
			toastr.warning("", this.props.t("times.chat.saveTimeBeforeMessage"));
			return;
		}

		this.setState((prev) => ({
			files: [...prev.files, ...acceptedFiles],
		}));
	};

	removeFile = (file) => {
		this.setState((prev) => ({
			files: prev.files.filter(
				(f) =>
					f.name !== file.name ||
					f.size !== file.size ||
					f.lastModified !== file.lastModified
			),
		}));
	};

	getAttachment = async (attachment) => {
		const { time } = this.state;
		const response = await timesService.getAttachment(time.id, attachment.url);
		const url = window.URL.createObjectURL(response);
		const a = document.createElement("a");
		document.body.appendChild(a);
		a.href = url;
		a.download = attachment.name;
		a.click();
		document.body.removeChild(a);
		window.URL.revokeObjectURL(url);
	};

	formatFileSize = (size) => {
		if (!size && size !== 0) return "";
		if (size >= 1000000) {
			return `${(size / 1000000).toFixed(1)} MB`;
		}
		return `${(size / 1000).toFixed(1)} KB`;
	};

	getFileTypeLabel = (name) => {
		const parts = (name || "").split(".");
		if (parts.length < 2) return "FILE";
		return parts.pop().toUpperCase();
	};

	buildTimelineItems = (state = this.state) => {
		const messages = getChatMessages(state.chat).map((message) => ({
			type: "message",
			key: `message-${message.id || getMessageCreatedAt(message)}-${getMessageText(message)}`,
			createdAt: getMessageCreatedAt(message),
			data: message,
		}));

		const attachments = (state.time?.attachments || []).map((attachment) => ({
			type: "attachment",
			key: `attachment-${attachment.url}`,
			createdAt: getAttachmentCreatedAt(attachment),
			data: attachment,
		}));

		return [...messages, ...attachments].sort((a, b) => {
			if (!a.createdAt && !b.createdAt) return 0;
			if (!a.createdAt) return 1;
			if (!b.createdAt) return -1;
			return moment.utc(a.createdAt).valueOf() - moment.utc(b.createdAt).valueOf();
		});
	};

	groupTimelineByDate = (items) => {
		const groups = [];
		let currentDateKey = null;
		let currentGroup = null;

		items.forEach((item) => {
			const dateKey = item.createdAt
				? moment.utc(item.createdAt).local().format("YYYY-MM-DD")
				: "unknown";

			if (dateKey !== currentDateKey) {
				currentDateKey = dateKey;
				currentGroup = { dateKey, createdAt: item.createdAt, items: [] };
				groups.push(currentGroup);
			}
			currentGroup.items.push(item);
		});

		return groups;
	};

	formatDateSeparator = (createdAt) => {
		if (!createdAt) return "";
		return moment.utc(createdAt).local().format("dddd, D [de] MMMM");
	};

	formatTimeRegistrationLabel = (time) => {
		const day = moment.utc(time.start).local().format("dddd");
		const start = moment.utc(time.start).local().format("H:mm");
		const end = time.end
			? moment.utc(time.end).local().format("H:mm")
			: "--:--";
		return this.props.t("times.chat.timeRegistrationLabel", { day, start, end });
	};

	renderPendingAttachmentChip = (file, onRemove) => {
		const name = file.name;
		const size = file.size;

		return (
			<div className="time-chat-modal-pending-chip">
				<div className="time-chat-modal-pending-chip-icon">
					<FiFileText size={16} />
				</div>
				<div className="time-chat-modal-pending-chip-info" title={name}>
					<span className="time-chat-modal-pending-chip-line">
						<span className="time-chat-modal-pending-chip-name">{name}</span>
						<span className="time-chat-modal-pending-chip-meta">
							{" · "}
							{this.getFileTypeLabel(name)}
							{size != null ? ` · ${this.formatFileSize(size)}` : ""}
						</span>
					</span>
				</div>
				<button
					type="button"
					className="time-chat-modal-pending-chip-remove"
					onClick={() => onRemove?.(file)}
					aria-label={this.props.t("common.delete")}
				>
					<FiX size={14} />
				</button>
			</div>
		);
	};

	renderAttachmentCard = (attachment, { isPending = false, onRemove } = {}) => {
		const name = attachment.name;
		const size = attachment.size;

		return (
			<div
				className={`time-chat-modal-attachment-card${
					isPending ? " time-chat-modal-attachment-card-pending" : ""
				}`}
			>
				<div className="time-chat-modal-attachment-icon">
					<FiFileText size={20} />
				</div>
				<div className="time-chat-modal-attachment-info">
					<span className="time-chat-modal-attachment-name" title={name}>
						{name}
					</span>
					<span className="time-chat-modal-attachment-meta">
						{this.getFileTypeLabel(name)}
						{size != null ? ` · ${this.formatFileSize(size)}` : ""}
					</span>
				</div>
				{isPending ? (
					<button
						type="button"
						className="time-chat-modal-attachment-remove"
						onClick={() => onRemove?.(attachment)}
						aria-label={this.props.t("common.delete")}
					>
						<FiX size={16} />
					</button>
				) : (
					<button
						type="button"
						className="time-chat-modal-attachment-download"
						onClick={() => this.getAttachment(attachment)}
						aria-label={this.props.t("times.chat.downloadAttachment")}
					>
						<FiDownload size={16} />
					</button>
				)}
			</div>
		);
	};

	renderMessage = (message) => {
		const currentUserId = this.getCurrentUserId();
		const authorId = getMessageAuthorId(message);
		const isOwn = authorId != null && String(authorId) === String(currentUserId);
		const employee = message.employee || message.author;
		const createdAt = getMessageCreatedAt(message);
		const timeLabel = createdAt
			? moment.utc(createdAt).local().format("HH:mm")
			: "";
		const text = getMessageText(message);
		const isNote = getMessageIsNote(message);

		if (isNote) {
			return (
				<div
					key={message.id || `${authorId}-${createdAt}-${text}`}
					className="time-chat-modal-note"
				>
					<div className="time-chat-modal-note-label">
						<FiEdit3 size={13} aria-hidden="true" />
						{this.props.t("times.chat.internalNote")}
					</div>
					<div className="time-chat-modal-note-text">{text}</div>
					{timeLabel && (
						<span className="time-chat-modal-note-time">{timeLabel}</span>
					)}
				</div>
			);
		}

		return (
			<div
				key={message.id || `${authorId}-${createdAt}-${text}`}
				className={`time-chat-modal-message-row ${
					isOwn
						? "time-chat-modal-message-row-own"
						: "time-chat-modal-message-row-other"
				}`}
			>
				{!isOwn && (
					<div className="time-chat-modal-avatar">
						<Avatar
							userId={authorId}
							name={getEmployeeDisplayName(employee)}
							title={getEmployeeDisplayName(employee)}
							style={{ width: 32, height: 32 }}
						/>
					</div>
				)}
				<div className="time-chat-modal-bubble">
					{text}
					{timeLabel && (
						<span className="time-chat-modal-bubble-time">{timeLabel}</span>
					)}
				</div>
			</div>
		);
	};

	renderAttachmentMessage = (attachment) => {
		const currentUserId = this.getCurrentUserId();
		const authorId = this.getAttachmentAuthorIdForRender(attachment);
		const author = this.getAttachmentAuthorForRender(attachment);
		const isOwn =
			authorId != null && currentUserId != null
				? String(authorId) === String(currentUserId)
				: false;
		const createdAt = getAttachmentCreatedAt(attachment);
		const timeLabel = createdAt
			? moment.utc(createdAt).local().format("HH:mm")
			: "";

		return (
			<div
				key={attachment.url}
				className={`time-chat-modal-message-row ${
					isOwn
						? "time-chat-modal-message-row-own"
						: "time-chat-modal-message-row-other"
				}`}
			>
				{!isOwn && authorId != null && (
					<div className="time-chat-modal-avatar">
						<Avatar
							userId={authorId}
							name={getEmployeeDisplayName(author)}
							title={getEmployeeDisplayName(author)}
							style={{ width: 32, height: 32 }}
						/>
					</div>
				)}
				<div className="time-chat-modal-bubble time-chat-modal-bubble-attachment">
					{this.renderAttachmentCard(attachment)}
					{timeLabel && (
						<span className="time-chat-modal-bubble-time">{timeLabel}</span>
					)}
				</div>
			</div>
		);
	};

	renderTimeline = () => {
		const { isLoadingChat, isReadonly } = this.state;
		const timeline = this.buildTimelineItems();

		if (isLoadingChat && timeline.length === 0) {
			return (
				<div className="time-chat-modal-empty">
					{this.props.t("times.chat.loading")}
				</div>
			);
		}

		if (timeline.length === 0) {
			return (
				<div className="time-chat-modal-empty">
					{isReadonly
						? this.props.t("times.chat.emptyReadonly")
						: this.props.t("times.chat.empty")}
				</div>
			);
		}

		return this.groupTimelineByDate(timeline).map((group) => (
			<React.Fragment key={group.dateKey}>
				{group.createdAt && (
					<div className="time-chat-modal-date-separator">
						{this.formatDateSeparator(group.createdAt)}
					</div>
				)}
				{group.items.map((item) =>
					item.type === "message"
						? this.renderMessage(item.data)
						: this.renderAttachmentMessage(item.data)
				)}
			</React.Fragment>
		));
	};

	render() {
		const { time, isReadonly, draftMessage, files, isSendingMessage, isNote } =
			this.state;
		const employee = this.getEmployeeForHeader();
		const employeeName = getEmployeeDisplayName(employee);
		const isTimeUnsaved = this.isTimeUnsaved();
		const canEditChat = !isReadonly && !isTimeUnsaved;
		const { isLoadingChat } = this.state;
		const hasChat = Boolean(getTimeChatId(time) || this.state.chat?.id);
		const canSend =
			!isSendingMessage &&
			(draftMessage.trim().length > 0 || (!isNote && files.length > 0));

		return (
			<div className="time-chat-modal">
				<div className="modal-body">
					<div className="time-chat-modal-context">
						<div>
							<span className="time-chat-modal-context-label">
								{this.props.t("times.chat.conversationWith")}{" "}
							</span>
							<strong>{employeeName || this.props.t("common.employee")}</strong>
						</div>
						<div className="time-chat-modal-context-actions">
							<div className="time-chat-modal-context-time">
								{this.formatTimeRegistrationLabel(time)}
							</div>
							{hasChat && (
								<button
									type="button"
									className="time-chat-modal-sync-btn"
									onClick={this.handleSyncChat}
									disabled={isLoadingChat}
									title={this.props.t("times.chat.sync")}
									aria-label={this.props.t("times.chat.sync")}
								>
									<FiRefreshCw
										size={16}
										className={isLoadingChat ? "time-chat-modal-sync-icon-spinning" : ""}
									/>
								</button>
							)}
						</div>
					</div>

					{isTimeUnsaved && !isReadonly && (
						<div className="time-chat-modal-warning" role="alert">
							{this.props.t("times.chat.saveTimeBeforeMessage")}
						</div>
					)}

					<div
						className="time-chat-modal-messages"
						ref={this.messagesContainerRef}
					>
						{this.renderTimeline()}
						<div ref={this.messagesEndRef} />
					</div>

					{canEditChat && (
						<div className="time-chat-modal-composer-area">
							<div className="time-chat-modal-composer-mode-row">
								<div
									className="time-chat-modal-composer-mode"
									role="group"
									aria-label={this.props.t("times.chat.composerMode")}
								>
									<button
										type="button"
										className={`time-chat-modal-mode-btn${!isNote ? " time-chat-modal-mode-btn-active" : ""}`}
										onClick={() => this.handleComposerModeChange(false)}
										aria-pressed={!isNote}
									>
										<FiMessageCircle size={15} aria-hidden="true" />
										{this.props.t("times.chat.messageMode")}
									</button>
									<button
										type="button"
										className={`time-chat-modal-mode-btn time-chat-modal-mode-btn-note${isNote ? " time-chat-modal-mode-btn-active" : ""}`}
										onClick={() => this.handleComposerModeChange(true)}
										aria-pressed={isNote}
										disabled={files.length > 0}
										title={files.length > 0 ? this.props.t("times.chat.noteAttachmentsNotAllowed") : undefined}
									>
										<FiEdit3 size={15} aria-hidden="true" />
										{this.props.t("times.chat.noteMode")}
									</button>
								</div>
								<span className="time-chat-modal-mode-help">
									{isNote
										? this.props.t("times.chat.noteVisibility", { name: employeeName })
										: this.props.t("times.chat.messageVisibility", { name: employeeName })}
								</span>
							</div>
							<div className={`time-chat-modal-composer${isNote ? " time-chat-modal-composer-note" : ""}`}>
							<Dropzone onDrop={this.onDrop} disabled={isTimeUnsaved} noClick>
								{({ getRootProps, getInputProps, open }) => (
									<>
										<button
											type="button"
											className="time-chat-modal-attach-btn"
											onClick={open}
											disabled={isSendingMessage || isNote}
											aria-label={this.props.t("times.chat.addAttachment")}
										>
											<FiPlus size={20} />
										</button>
										<div {...getRootProps({ style: { display: "none" } })}>
											<input {...getInputProps()} />
										</div>
									</>
								)}
							</Dropzone>

							<div className="time-chat-modal-composer-input-wrap">
								{files.length > 0 && (
									<div className="time-chat-modal-pending-attachments">
										{files.map((file) => (
											<div
												key={`${file.name}-${file.size}-${file.lastModified}`}
												className="time-chat-modal-pending-attachment"
											>
												{this.renderPendingAttachmentChip(file, this.removeFile)}
											</div>
										))}
									</div>
								)}
								<div className="time-chat-modal-composer-input-row">
									<input
										ref={this.composerRef}
										type="text"
										className="time-chat-modal-composer-input"
										value={draftMessage}
										onChange={this.handleDraftChange}
										onKeyDown={this.handleDraftKeyDown}
										placeholder={this.props.t(
											isNote ? "times.chat.notePlaceholder" : "times.chat.messagePlaceholder"
										)}
										disabled={isSendingMessage}
									/>
									{QUICK_EMOJIS.length > 0 && (
										<>
											<button
												type="button"
												className="time-chat-modal-emoji-btn"
												onClick={this.toggleEmojiPicker}
												aria-label={this.props.t("times.chat.addEmoji")}
												aria-expanded={this.state.showEmojiPicker}
												disabled={isSendingMessage}
											>
												<BsEmojiSmile size={18} />
											</button>
											{this.state.showEmojiPicker && (
												<div className="time-chat-modal-emoji-picker">
													{QUICK_EMOJIS.map((emoji) => (
														<button
															key={emoji}
															type="button"
															className="time-chat-modal-emoji-option"
															onClick={() => this.appendEmoji(emoji)}
														>
															{emoji}
														</button>
													))}
												</div>
											)}
										</>
									)}
								</div>
							</div>
							<button
								type="button"
								className="time-chat-modal-send-btn"
								onClick={this.handleSendMessage}
								disabled={!canSend}
								aria-label={this.props.t("times.chat.send")}
							>
								<FiSend size={18} />
							</button>
							</div>
						</div>
					)}
				</div>

				{isReadonly && (
					<div className="modal-footer">
						<button
							type="button"
							onClick={this.handleCloseModal}
							className="btn btn-outline-primary"
						>
							{this.props.t("common.close")}
						</button>
					</div>
				)}
			</div>
		);
	}
}

const mapStateToProps = (state) => ({
	...state.timesReducer,
	user: state.authReducer?.user,
});

export default connect(mapStateToProps)(withTranslation()(TimeObservationEdit));
